import React from "react";
import Footer from "./Footer";
import * as LucideIcons from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import useStickyButton from "../hooks/useStickyButton";

const imageAssets = {
  "livenessCheckRequirement": require("../../../assets/images/plus/Liveness_Check_Requirement.png"),
};


const GuidelineLiveness = (props) => {
  const { onNext, uiConfig } = props;
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  // Safe access
  const global = uiConfig?.Global || {};
  const guidelineLiveness = uiConfig?.GuidelineLiveness || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.background || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.background || "#ffffff"; // white
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getTitleHTML = () => {
    const titleConfig = guidelineLiveness?.Title?.Text;
    if (titleConfig) {
      return titleConfig;
    }
    return `<h2 style="font-size: 1.25rem; font-weight: bold; margin-bottom: 1rem; text-align: center; color: ${textColor};">
      Prepare for Liveness Check
    </h2>`;
  };

  const getDescriptionHTML = () => {
    const descriptionConfig = guidelineLiveness?.Description?.Text;
    if (descriptionConfig) {
      return descriptionConfig;
    }
    return `<p style="font-size: 1rem; margin-bottom: 1rem; text-align: left; max-width: 65ch; color: ${textColor};">
      We'll now verify that you are physically present. This quick security step helps protect your identity.
    </p>`;
  };

  const getChecklistConfig = () => {
    const checklistConfig = guidelineLiveness?.Checklist;
    
    const defaultItems = [
      { text: "Find a well-lit area", icon: null },
      { text: "Position your face clearly in the frame", icon: null },
      { text: "Make sure you are alone in the camera view", icon: null },
    ];
    
    return {
      items: checklistConfig?.Items && checklistConfig.Items.length > 0 ? checklistConfig.Items : defaultItems,
      itemSpacing: checklistConfig?.ItemSpacing || "0.5rem",
      iconColor: checklistConfig?.IconColor || "#2563eb",
      iconSize: checklistConfig?.IconSize || 24
    };
  };

  const getSampleImageConfig = () => {
    const sampleImage = guidelineLiveness?.SampleImage || {};
    
    return {
      show: sampleImage?.Show || false,
      imageUrl: sampleImage?.Image?.Url || "",
      imageStyle: sampleImage?.Image?.Style || "height: auto; width: 20rem; max-height: 200px; object-fit: contain; border-radius: 0.5rem; box-shadow: 0 2px 8px rgba(0,0,0,0.5);",
      captionText: sampleImage?.Caption?.Text || "",
      captionStyle: sampleImage?.Caption?.Style || `font-size: 0.875rem; text-align: center; font-weight: bold; margin-top: 0.25rem; color: ${textColor};`
    };
  };

  const getButtonConfig = () => {
    const globalButton = global?.button || {};
    const buttonConfig = guidelineLiveness?.Button || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `width: 66.666667%; padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (buttonConfig?.Style) {
      const parsedButtonStyle = buttonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: buttonConfig?.Text || globalButton?.Text || "Continue",
      style: mergedStyleString,
      icon: {
        show: buttonConfig?.Icon?.Show !== undefined ? buttonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : true),
        position: buttonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: buttonConfig?.Icon?.Name || globalButton?.Icon?.Name || "ChevronRight",
        className: buttonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "w-5 h-5"
      }
    };
  };

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <SafeHTML html={getTitleHTML()} />

      <SafeHTML html={getDescriptionHTML()} />

      {/* Checklist */}
      {(() => {
        const checklistConfig = getChecklistConfig();
        return (
          <ul style={{ display: "flex", flexDirection: "column", gap: checklistConfig.itemSpacing, listStyle: "none", padding: 0, margin: "0 0 2rem 0" }}>
            {checklistConfig.items.map((item, index) => {
              const IconComponent = item.icon && LucideIcons[item.icon] ? LucideIcons[item.icon] : LucideIcons.ChevronRight;
              
              return (
                <li key={index} className="flex items-start space-x-2">
                  <IconComponent 
                    style={{ 
                      color: checklistConfig.iconColor,
                      minWidth: `${checklistConfig.iconSize}px`,
                      minHeight: `${checklistConfig.iconSize}px`,
                      width: `${checklistConfig.iconSize}px`,
                      height: `${checklistConfig.iconSize}px`
                    }} 
                  />
                  <span style={{ color: textColor }}>{item.text}</span>
                </li>
              );
            })}
          </ul>
        );
      })()}

      {/* Sample Image (optional) */}
      {(() => {
        const sampleImageConfig = getSampleImageConfig();
        if (!sampleImageConfig.show || !sampleImageConfig.imageUrl) return null;
        
        // Determine image source: local asset identifier or URL
        const imageSrc = imageAssets[sampleImageConfig.imageUrl] || sampleImageConfig.imageUrl;
        
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "2rem" }}>
            <div style={{ marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>
              <img src={imageSrc} style={sampleImageConfig.imageStyle.split(';').reduce((acc, style) => {
                const [key, value] = style.split(':').map(s => s.trim());
                if (key && value) {
                  const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                  acc[camelKey] = value;
                }
                return acc;
              }, {})} alt="Sample" />
            </div>
            {sampleImageConfig.captionText && (
              <p style={sampleImageConfig.captionStyle.split(';').reduce((acc, style) => {
                const [key, value] = style.split(':').map(s => s.trim());
                if (key && value) {
                  const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                  acc[camelKey] = value;
                }
                return acc;
              }, {})}>
                {sampleImageConfig.captionText}
              </p>
            )}
          </div>
        );
      })()}

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      {/* Button */}
      <div 
        ref={buttonContainerRef}
        style={{
          position: isSticky ? "fixed" : "relative",
          bottom: isSticky ? "0" : "auto",
          left: isSticky ? "0" : "auto",
          right: isSticky ? "0" : "auto",
          padding: isSticky ? "1rem" : "0",
          background: "transparent",
          zIndex: isSticky ? "50" : "auto",
          transition: "all 0.3s ease-in-out",
          pointerEvents: isSticky ? "none" : "auto",
        }}
      >
        {(() => {
          const btnConfig = getButtonConfig();
          const IconComponent = btnConfig.icon.show && LucideIcons[btnConfig.icon.name] ? LucideIcons[btnConfig.icon.name] : null;
          
          const buttonStyle = btnConfig.style.split(';').reduce((acc, style) => {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
              const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              acc[camelKey] = value;
            }
            return acc;
          }, {});

          return (
            <button
              style={{ ...buttonStyle, pointerEvents: 'auto' }}
              onClick={onNext}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {btnConfig.icon.position === "start" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
              <span>{btnConfig.text}</span>
              {btnConfig.icon.position === "end" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
            </button>
          );
        })()}
      </div>

      {showFooter && (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      )}
    </div>
  );
};

export default GuidelineLiveness;
